import { useEffect, useState, type RefObject } from "react";
import { Button } from "../Button";

/** Marks the element the Now button scrolls to (a view's now-line). */
export const NOW_MARKER_ATTRIBUTE = "data-now-marker";

interface NowButtonProps {
  /** The Planner pane; the current view's now marker is looked up inside. */
  containerRef: RefObject<HTMLElement | null>;
  /**
   * Changes whenever the marker may have mounted, moved or unmounted (mode,
   * visible dates, today's date), so the observer re-attaches.
   */
  markerKey: string;
  /**
   * Brings today into the view when it isn't rendered (another day, leg or
   * four-week window). Without it the button stays disabled in that case.
   */
  onNavigateToToday?: () => void;
  /** Phone header with day or leg navigation: icon only. */
  iconOnly?: boolean;
}

/**
 * Planner header control that returns the current view to now while the trip
 * is under way. It scrolls the view's now marker into view, or asks the
 * Planner to show today when the marker isn't rendered. Disabled while the
 * marker is already on screen.
 */
export function NowButton({
  containerRef,
  markerKey,
  onNavigateToToday,
  iconOnly = false,
}: NowButtonProps) {
  const [hasMarker, setHasMarker] = useState(false);
  // Assume on screen until observed, so the button settles from disabled to
  // enabled rather than flashing enabled on every view change.
  const [markerOnScreen, setMarkerOnScreen] = useState(true);

  useEffect(() => {
    const marker = containerRef.current?.querySelector(
      `[${NOW_MARKER_ATTRIBUTE}]`,
    );
    setHasMarker(!!marker);
    setMarkerOnScreen(true);
    if (!marker || typeof IntersectionObserver === "undefined") return;

    // A null root still clips the marker by each scrolling ancestor
    const observer = new IntersectionObserver(([entry]) => {
      if (entry) setMarkerOnScreen(entry.isIntersecting);
    });
    observer.observe(marker);
    return () => observer.disconnect();
  }, [containerRef, markerKey]);

  const disabled = hasMarker ? markerOnScreen : !onNavigateToToday;
  const title = hasMarker
    ? markerOnScreen
      ? "The current time is on screen"
      : "Jump to the current time"
    : onNavigateToToday
      ? "Jump to the current time"
      : "The current time isn't in this view";

  const jump = () => {
    const marker = containerRef.current?.querySelector(
      `[${NOW_MARKER_ATTRIBUTE}]`,
    );
    if (marker) {
      marker.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    } else {
      onNavigateToToday?.();
    }
  };

  return (
    <Button
      variant={iconOnly ? "icon" : "secondary"}
      size="sm"
      icon="schedule"
      disabled={disabled}
      onClick={jump}
      title={title}
      aria-label={iconOnly ? "Now" : undefined}
      // The clock turns now-line red while there is somewhere to jump to
      className="shrink-0 [&:enabled>.material-symbols-outlined]:text-calendar-now"
      data-testid="now-button"
    >
      {iconOnly ? undefined : "Now"}
    </Button>
  );
}
